import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { MdSearch, MdError, MdFilterList, MdCategory, MdEdit, MdDelete, MdClose } from 'react-icons/md';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { fetchRcnReturns, updateRcnReturn, deleteRcnReturn } from 'api/rcnReturns';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, recordId, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">Confirm Deletion</h3>
          <button onClick={onClose} disabled={isDeleting} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed">✕</button>
        </div>
        <p className="mb-6 text-gray-600 dark:text-gray-300">Are you sure you want to delete this record? This action cannot be undone.</p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={isDeleting} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed">Cancel</button>
          <button onClick={onConfirm} disabled={isDeleting} className="flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed">
            {isDeleting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const SIZE_OPTIONS = [15, 17, 19, 21, 23, 25, 28].flatMap((s) => [`+${s}`, `-${s}`]);
const ZONE_OPTIONS = [
  ...Array.from({ length: 16 }, (_, i) => `R${i + 1}`),
  ...Array.from({ length: 16 }, (_, i) => `L${i + 1}`),
];
const RCN_TYPE_OPTIONS = ["Fair Trade", "Organic", "Conventional"];

const EditRecordModal = ({ isOpen, onClose, record, onSave, isSaving }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (record) {
      setForm({
        date: record.date ? String(record.date).slice(0, 10) : '',
        rcn_type: record.rcn_type || '',
        moisture: record.moisture ?? '',
        origin: record.origin || '',
        outturn: record.outturn ?? '',
        size: record.size || '',
        quantity: record.quantity ?? '',
        zone: record.zone || '',
        real_weight: record.real_weight ?? '',
        number_of_bags: record.number_of_bags ?? '',
      });
    }
  }, [record]);

  if (!isOpen || !record) return null;

  const handleField = (e) => {
    const { id, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [id]: value };
      if (id === 'quantity' || id === 'number_of_bags') {
        const quantity = parseFloat(next.quantity) || 0;
        const bags = parseInt(next.number_of_bags) || 0;
        next.real_weight = quantity > 0 || bags > 0 ? (quantity - bags * 3).toFixed(2) : '';
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      id: record.id,
      date: form.date || null,
      rcn_type: form.rcn_type || null,
      moisture: form.moisture !== '' ? parseFloat(form.moisture) : null,
      origin: form.origin || null,
      outturn: form.outturn !== '' ? parseFloat(form.outturn) : null,
      size: form.size || null,
      quantity: form.quantity !== '' ? parseFloat(form.quantity) : null,
      zone: form.zone || null,
      real_weight: form.real_weight !== '' ? parseFloat(form.real_weight) : null,
      number_of_bags: form.number_of_bags !== '' ? parseInt(form.number_of_bags) : null,
    });
  };

  const inputClass = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={isSaving ? undefined : onClose}></div>
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">Edit Return Record #{record.id}</h3>
          <button onClick={onClose} disabled={isSaving} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700 disabled:opacity-50">
            <MdClose className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Date *</label>
            <input id="date" type="date" value={form.date || ''} onChange={handleField} required className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">RCN Type *</label>
            <select id="rcn_type" value={form.rcn_type || ''} onChange={handleField} required className={inputClass}>
              <option value="">Select RCN type</option>
              {RCN_TYPE_OPTIONS.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Origin</label>
            <input id="origin" type="text" value={form.origin || ''} onChange={handleField} className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Number of Bags</label>
            <input id="number_of_bags" type="number" value={form.number_of_bags ?? ''} onChange={handleField} className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Moisture</label>
            <input id="moisture" type="number" step="0.01" value={form.moisture ?? ''} onChange={handleField} className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Outturn</label>
            <input id="outturn" type="number" step="0.01" value={form.outturn ?? ''} onChange={handleField} className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Quantity (KG) *</label>
            <input id="quantity" type="number" step="0.01" value={form.quantity ?? ''} onChange={handleField} required className={inputClass} />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Real Quantity (KG)</label>
            <input id="real_weight" type="number" step="0.01" value={form.real_weight ?? ''} onChange={handleField} readOnly className={`${inputClass} bg-gray-100 dark:bg-navy-900`} />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Size *</label>
            <select id="size" value={form.size || ''} onChange={handleField} required className={inputClass}>
              <option value="">Select size</option>
              {SIZE_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-sm font-medium text-navy-700 dark:text-gray-300">Zone *</label>
            <select id="zone" value={form.zone || ''} onChange={handleField} required className={inputClass}>
              <option value="">Select zone</option>
              {ZONE_OPTIONS.map((z) => (<option key={z} value={z}>{z}</option>))}
            </select>
          </div>
          <div className="col-span-1 mt-2 flex justify-end gap-3 sm:col-span-2">
            <button type="button" onClick={onClose} disabled={isSaving} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={isSaving} className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
              {isSaving ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReturnsRecords = () => {
  const TABLE_HEAD = ["ID", "Date", "Type", "Origin", "Size", "Quantity", "Bags", "Outturn", "Moisture", "Zone", "Actions"];
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', size: '', origin: '', type: '' });
  const itemsPerPage = 10;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [downloadingQrId, setDownloadingQrId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Sample return records (kept as fallback reference)
  const sampleData = [
    { id: 1, created_at: '2026-03-01T10:00:00Z', rcn_type: 'Fair Trade', origin: 'Site A', size: 'size_17', quantity: '150.50', number_of_bags: '10', outturn: '45.5', moisture: '6.2', zone: 'R1', real_weight: '120.50' },
    { id: 2, created_at: '2026-03-02T11:30:00Z', rcn_type: 'Organic', origin: 'Site B', size: 'size_19', quantity: '200.00', number_of_bags: '12', outturn: '46.0', moisture: '5.8', zone: 'L2', real_weight: '164.00' },
    { id: 3, created_at: '2026-03-03T09:15:00Z', rcn_type: 'Conventional', origin: 'Site C', size: 'size_15', quantity: '75.00', number_of_bags: '5', outturn: '44.0', moisture: '6.5', zone: 'R3', real_weight: '60.00' },
    { id: 4, created_at: '2026-03-04T14:00:00Z', rcn_type: 'Fair Trade', origin: 'Site D', size: 'size_21', quantity: '300.75', number_of_bags: '20', outturn: '47.2', moisture: '5.4', zone: 'L4', real_weight: '240.75' }
  ];

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchRcnReturns()
      .then((res) => {
        if (!mounted) return;
        // fetchRcnReturns returns an array of records
        setData(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        console.error(err);
        if (!mounted) return;
        setError(err.message || 'Failed to load return records');
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const calculateSummary = (arr) => {
    if (!Array.isArray(arr)) return { totalReturns: 0, totalWeight: '0.00', totalBags: 0, avgOutturn: '0.00' };
    const totalReturns = arr.length;
    const totalWeight = arr.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    const totalBags = arr.reduce((sum, item) => sum + (parseInt(item.number_of_bags) || 0), 0);
    const avgOutturn = arr.reduce((sum, item) => sum + (parseFloat(item.outturn) || 0), 0) / (arr.length || 1);
    return {
      totalReturns,
      totalWeight: new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalWeight),
      totalBags,
      avgOutturn: avgOutturn.toFixed(2)
    };
  };

  const uniqueSizes = Array.isArray(data) ? [...new Set(data.map(item => item.size))] : [];
  const uniqueOrigins = Array.isArray(data) ? [...new Set(data.map(item => item.origin))] : [];
  const uniqueTypes = Array.isArray(data) ? [...new Set(data.map(item => item.rcn_type))] : [];

  const filteredData = Array.isArray(data) ? data.filter(item => {
    const matchesSearch = (item.origin && item.origin.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.size && item.size.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.rcn_type && item.rcn_type.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesDate = (!filters.startDate || new Date(item.created_at) >= new Date(filters.startDate)) && (!filters.endDate || new Date(item.created_at) <= new Date(filters.endDate));
    const matchesSize = !filters.size || item.size === filters.size;
    const matchesOrigin = !filters.origin || item.origin === filters.origin;
    const matchesType = !filters.type || item.rcn_type === filters.type;

    return matchesSearch && matchesDate && matchesSize && matchesOrigin && matchesType;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [];

  const summary = calculateSummary(filteredData);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleDelete = (id) => { setRecordToDelete(id); setIsDeleteModalOpen(true); };

  const handleEdit = (record) => { setRecordToEdit(record); setIsEditModalOpen(true); };

  const handleSaveEdit = async (payload) => {
    setIsSaving(true);
    try {
      const res = await updateRcnReturn(payload);
      const updated = res.data || payload;
      setData((prev) => prev.map((item) => (item.id === payload.id ? { ...item, ...updated } : item)));
      toast.success(res.message || 'Record updated');
      setIsEditModalOpen(false);
      setRecordToEdit(null);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Error updating record');
    } finally {
      setIsSaving(false);
    }
  };

  const navigate = useNavigate();

  const handleDownloadQr = async (item) => {
    try {
      setDownloadingQrId(item.id);
      const recordUrl = `${window.location.origin}/admin/warehouse/calibrated/${item.id}`;
      const qrApi = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(recordUrl)}`;
      const res = await fetch(qrApi);
      if (!res.ok) throw new Error('QR generation failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `return-${item.id}-qrcode.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success('QR code downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to download QR code');
    } finally {
      setDownloadingQrId(null);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await deleteRcnReturn(recordToDelete);
      setData(prev => prev.filter(item => item.id !== recordToDelete));
      toast.success(res.message || 'Record deleted');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Error deleting record');
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Returns</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalReturns}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Weight</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalWeight} kg</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Bags</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalBags}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-orange-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Outturn</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.avgOutturn}%</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-white">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Date Range</label>
              <div className="flex gap-2">
                <input type="date" value={filters.startDate} onChange={(e) => setFilters({...filters, startDate: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700" />
                <input type="date" value={filters.endDate} onChange={(e) => setFilters({...filters, endDate: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700" />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Size</label>
              <select value={filters.size} onChange={(e) => setFilters({...filters, size: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700">
                <option value="">All Sizes</option>
                {uniqueSizes.map(size => (<option key={size} value={size}>{size}</option>))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Origin</label>
              <select value={filters.origin} onChange={(e) => setFilters({...filters, origin: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700">
                <option value="">All Origins</option>
                {uniqueOrigins.map(origin => (<option key={origin} value={origin}>{origin}</option>))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Type</label>
              <select value={filters.type} onChange={(e) => setFilters({...filters, type: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700">
                <option value="">All Types</option>
                {uniqueTypes.map(type => (<option key={type} value={type}>{type}</option>))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={() => setFilters({startDate: '', endDate: '', size: '', origin: '', type: ''})} className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600">Clear Filters</button>
          </div>
        </div>

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">Warehouse Return Records</h4>
            <p className="mt-1 text-sm text-gray-600">Track returned RCN items</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex h-10 items-center rounded-full bg-white px-4 dark:bg-navy-900">
              <span className="text-xl"><MdSearch className="h-5 w-5 text-gray-400" /></span>
              <input type="text" placeholder="Search..." className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none dark:text-white" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center"><div className="text-center"><div className="mb-4 text-5xl text-red-500"><MdError className="mx-auto h-16 w-16" /></div><p className="text-xl font-semibold text-gray-700 dark:text-white">Oops! Something went wrong</p><p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th key={index} className="border-b border-gray-200 pb-[10px] pr-4 text-start dark:!border-navy-700"><p className="text-sm font-bold tracking-wide text-gray-800 dark:text-white">{head}</p></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.id}</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{new Date(item.created_at).toLocaleDateString()}</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.rcn_type}</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.origin}</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.size}</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.real_weight} kg</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.number_of_bags}</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.outturn}%</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.moisture}%</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]"><p className="text-sm font-bold text-navy-700 dark:text-white">{item.zone}</p></td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(item.id)}
                          title="Delete"
                          className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-600 transition hover:bg-red-100 dark:bg-navy-700 dark:text-red-400"
                        >
                          <MdDelete className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries</p>
          <div className="flex gap-2">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${ currentPage === 1 ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700" }`}>First</button>
            <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${ currentPage === 1 ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700" }`}>Previous</button>
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              let pageNum;
              if (totalPages <= 5) pageNum = index + 1;
              else if (currentPage <= 3) pageNum = index + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + index;
              else pageNum = currentPage - 2 + index;
              return <button key={index} onClick={() => setCurrentPage(pageNum)} className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${ currentPage === pageNum ? "bg-brand-700 text-white" : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700" }`}>{pageNum}</button>;
            })}
            <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${ currentPage === totalPages || totalPages === 0 ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700" }`}>Next</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${ currentPage === totalPages || totalPages === 0 ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700" }`}>Last</button>
          </div>
        </div>

        <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={() => { setIsDeleteModalOpen(false); setRecordToDelete(null); }} onConfirm={confirmDelete} recordId={recordToDelete} isDeleting={isDeleting} />
        <EditRecordModal isOpen={isEditModalOpen} onClose={() => { setIsEditModalOpen(false); setRecordToEdit(null); }} record={recordToEdit} onSave={handleSaveEdit} isSaving={isSaving} />
      </Card>
    </div>
  );
};

export default ReturnsRecords;
